import React, { useState, useEffect } from "react";
import { ref, push, onValue, remove, update, set } from "firebase/database";
import { db } from "./firebase";
import { FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiUser, FiMail, FiShield } from "react-icons/fi";

const SuperAdmin = () => {
    const [admins, setAdmins] = useState({});
    const [loading, setLoading] = useState(true);
    const [isAddFormExpanded, setIsAddFormExpanded] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    // Form states
    const [adminName, setAdminName] = useState("");
    const [adminEmail, setAdminEmail] = useState("");
    const [adminId, setAdminId] = useState("");
    const [formError, setFormError] = useState("");

    // Fetch all admins from users node
    useEffect(() => {
        const usersRef = ref(db, "users");
        return onValue(usersRef, (snapshot) => {
            if (snapshot.exists()) {
                const usersData = snapshot.val();
                // Filter only admin users
                const adminUsers = {};
                Object.entries(usersData).forEach(([key, user]) => {
                    if (user.role === "admin") {
                        adminUsers[key] = user;
                    }
                });
                setAdmins(adminUsers);
            } else {
                setAdmins({});
            }
            setLoading(false);
        });
    }, []);

    // Add new admin
    const handleAddAdmin = async () => {
        setFormError("");

        if (!adminName.trim() || !adminEmail.trim() || !adminId.trim()) {
            setFormError("Please fill in all fields");
            return;
        }

        // Check if email already exists
        const emailExists = Object.values(admins).some(admin =>
            admin.email.toLowerCase() === adminEmail.toLowerCase()
        );

        if (emailExists) {
            setFormError("Email already exists");
            return;
        }

        // Check if ID already exists
        const idExists = Object.values(admins).some(admin =>
            admin.id === adminId
        );

        if (idExists) {
            setFormError("Admin ID already exists");
            return;
        }

        try {
            const newAdmin = {
                name: adminName.trim(),
                email: adminEmail.trim().toLowerCase(),
                id: adminId.trim(),
                role: "admin",
                student: false,
                createdAt: new Date().toISOString()
            };

            await push(ref(db, "users"), newAdmin);

            // Reset form
            setAdminName("");
            setAdminEmail("");
            setAdminId("");
            setIsAddFormExpanded(false);
            setFormError("");
        } catch (error) {
            setFormError("Error adding admin: " + error.message);
        }
    };

    // Update admin
    const handleUpdateAdmin = async (adminKey) => {
        setFormError("");

        if (!adminName.trim() || !adminEmail.trim() || !adminId.trim()) {
            setFormError("Please fill in all fields");
            return;
        }

        // Check if email already exists (excluding current admin)
        const emailExists = Object.entries(admins).some(([key, admin]) =>
            key !== adminKey && admin.email.toLowerCase() === adminEmail.toLowerCase()
        );

        if (emailExists) {
            setFormError("Email already exists");
            return;
        }

        // Check if ID already exists (excluding current admin)
        const idExists = Object.entries(admins).some(([key, admin]) =>
            key !== adminKey && admin.id === adminId
        );

        if (idExists) {
            setFormError("Admin ID already exists");
            return;
        }

        try {
            const updatedAdmin = {
                name: adminName.trim(),
                email: adminEmail.trim().toLowerCase(),
                id: adminId.trim(),
                role: "admin",
                student: false,
                updatedAt: new Date().toISOString()
            };

            await update(ref(db, `users/${adminKey}`), updatedAdmin);

            // Reset form
            setAdminName("");
            setAdminEmail("");
            setAdminId("");
            setEditingAdmin(null);
            setFormError("");
        } catch (error) {
            setFormError("Error updating admin: " + error.message);
        }
    };

    // Delete admin
    const handleDeleteAdmin = async (adminKey, adminName) => {
        if (window.confirm(`Are you sure you want to delete admin "${adminName}"?`)) {
            try {
                await remove(ref(db, `users/${adminKey}`));
            } catch (error) {
                alert("Error deleting admin: " + error.message);
            }
        }
    };

    // Start editing admin
    const startEditing = (adminKey, admin) => {
        setEditingAdmin(adminKey);
        setAdminName(admin.name || "");
        setAdminEmail(admin.email || "");
        setAdminId(admin.id || "");
        setFormError("");
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingAdmin(null);
        setAdminName("");
        setAdminEmail("");
        setAdminId("");
        setFormError("");
    };

    // Filter admins by search query
    const filteredAdmins = React.useMemo(() => {
        if (!searchQuery) return admins;

        const filtered = {};
        Object.entries(admins).forEach(([key, admin]) => {
            if (
                admin.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                admin.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                admin.id?.toLowerCase().includes(searchQuery.toLowerCase())
            ) {
                filtered[key] = admin;
            }
        });
        return filtered;
    }, [admins, searchQuery]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
            </div>
        );
    }

    return (
        <div className="p-4 text-black min-h-screen">
            <h1 className="text-3xl font-bold mb-6 text-black">Super Admin - Manage Admins</h1>

            {/* Search and Add Button */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <input
                    type="text"
                    placeholder="Search admins..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="p-2 border rounded-md"
                />
                <button
                    onClick={() => setIsAddFormExpanded(!isAddFormExpanded)}
                    className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700 flex items-center justify-center gap-2"
                >
                    <FiPlus />
                    {isAddFormExpanded ? "Cancel" : "Add New Admin"}
                </button>
            </div>

            {/* Add Admin Form */}
            {isAddFormExpanded && (
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <h2 className="text-xl font-semibold mb-4">Add New Admin</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Admin Name*</label>
                            <input
                                type="text"
                                value={adminName}
                                onChange={(e) => setAdminName(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                placeholder="Enter admin name"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Email*</label>
                            <input
                                type="email"
                                value={adminEmail}
                                onChange={(e) => setAdminEmail(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                placeholder="Enter email address"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Admin ID*</label>
                            <input
                                type="text"
                                value={adminId}
                                onChange={(e) => setAdminId(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                placeholder="Enter admin ID"
                                required
                            />
                        </div>
                    </div>
                    {formError && <div className="text-red-600 mt-2">{formError}</div>}
                    <div className="mt-4 flex justify-end space-x-2">
                        <button
                            onClick={() => setIsAddFormExpanded(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddAdmin}
                            className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                        >
                            Add Admin
                        </button>
                    </div>
                </div>
            )}

            {/* Admins List */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold">Admin Profiles ({Object.keys(filteredAdmins).length})</h2>
                </div>

                {Object.keys(filteredAdmins).length > 0 ? (
                    <div className="divide-y divide-gray-200">
                        {Object.entries(filteredAdmins).map(([adminKey, admin]) => (
                            <div key={adminKey} className="p-6 hover:bg-gray-50">
                                {editingAdmin === adminKey ? (
                                    // Edit Form
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Admin Name*</label>
                                                <input
                                                    type="text"
                                                    value={adminName}
                                                    onChange={(e) => setAdminName(e.target.value)}
                                                    className="w-full p-2 border rounded-md"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Email*</label>
                                                <input
                                                    type="email"
                                                    value={adminEmail}
                                                    onChange={(e) => setAdminEmail(e.target.value)}
                                                    className="w-full p-2 border rounded-md"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Admin ID*</label>
                                                <input
                                                    type="text"
                                                    value={adminId}
                                                    onChange={(e) => setAdminId(e.target.value)}
                                                    className="w-full p-2 border rounded-md"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        {formError && <div className="text-red-600">{formError}</div>}
                                        <div className="flex justify-end space-x-2">
                                            <button
                                                onClick={cancelEditing}
                                                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() => handleUpdateAdmin(adminKey)}
                                                className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // Display Mode
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
                                                <FiUser className="text-teal-600 text-xl" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">{admin.name}</h3>
                                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                    <div className="flex items-center space-x-1">
                                                        <FiMail className="text-gray-400" />
                                                        <span>{admin.email}</span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        <FiShield className="text-gray-400" />
                                                        <span>ID: {admin.id}</span>
                                                    </div>
                                                </div>
                                                {admin.createdAt && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Created: {new Date(admin.createdAt).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => startEditing(adminKey, admin)}
                                                className="text-teal-600 hover:text-teal-800 p-2"
                                                title="Edit"
                                            >
                                                <FiEdit2 className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteAdmin(adminKey, admin.name)}
                                                className="text-red-600 hover:text-red-800 p-2"
                                                title="Delete"
                                            >
                                                <FiTrash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="p-8 text-center text-gray-500">
                        <FiUser className="mx-auto text-4xl text-gray-400 mb-4" />
                        <p className="text-lg">No admins found</p>
                        <p className="text-sm mt-2">
                            {searchQuery ? "Try adjusting your search criteria" : "Add your first admin to get started"}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperAdmin; 